import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Stack,
  Typography,
  SxProps,
  Theme,
} from '@mui/material';
import { ChevronDown, FilePlus, Plus } from 'lucide-react';
import { Modal } from '../../../../../ui/modal/modal';
import styles from '../../../../assessment/create/create_assessment.module.css';

export type ModalOption = {
  value: string;
  label: string;
  modalContent: JSX.Element | ((closeModal: () => void) => React.ReactNode);
  disabled?: boolean;
  icon?: React.ReactNode;
};

type DropdownWithModalProps = {
  options: ModalOption[];
  disabled?: boolean;
  label?: string;
  className?: string;
  sx?: SxProps<Theme>;
};

export const DropdownWithModal: React.FC<DropdownWithModalProps> = ({
  options,
  disabled = false,
  label = 'Generate Job Description',
  className,
  sx,
}) => {
  const [selectedValue, setSelectedValue] = useState('');
  const [selectedOption, setSelectedOption] =
    useState<ModalOption | undefined>(undefined);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const resolveModalContent = () => {
    const foundOption = options.find((o) => o.value === selectedValue);

    if (foundOption) {
      return typeof foundOption.modalContent === 'function'
        ? foundOption.modalContent(closeModal)
        : foundOption.modalContent;
    } else {
      return <></>;
    }
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleOptionSelect = (option: ModalOption) => {
    if (!option.disabled) {
      setSelectedValue(option.value);
      setSelectedOption(option);
      setIsModalVisible(true);
      handleCloseMenu();
    }
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setSelectedValue('');
  };

  const isOpen = Boolean(anchorEl);

  return (
    <Box className={className} sx={[{ position: 'relative', width: { xs: '100%', sm: 'auto' } }, ...(Array.isArray(sx) ? sx : [sx])]}>
      <Button
        variant="outlined"
        color="success"
        disabled={disabled}
        onClick={handleOpenMenu}
        startIcon={<FilePlus size={16} />}
        endIcon={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              transform: isOpen ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s',
            }}
          >
            <ChevronDown size={16} />
          </Box>
        }
        sx={{
          height: 38,
          minHeight: 38,
          width: { xs: '100%', sm: 'auto' },
          gap: 1,
          justifyContent: 'space-between',
          py: 0,
          px: 1.75,
          fontWeight: 600,
          fontSize: '0.8125rem',
          textTransform: 'none',
          borderRadius: 2,
          bgcolor: 'background.paper',
          borderColor: 'success.main',
          color: 'success.dark',
          whiteSpace: 'nowrap',
          '&:hover': {
            borderColor: 'success.dark',
            bgcolor: 'success.50',
          },
        }}
      >
        {label}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleCloseMenu}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            minWidth: 240,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          },
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            disabled={option.disabled}
            onClick={() => handleOptionSelect(option)}
            sx={{
              py: 1.25,
              px: 2,
              '&:hover': {
                bgcolor: 'success.50',
              },
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              {option.icon || (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: 'success.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Plus size={12} color="#059669" />
                </Box>
              )}
              <Typography variant="body2" fontWeight={500}>
                {option.label}
              </Typography>
            </Stack>
          </MenuItem>
        ))}
      </Menu>

      {/* Modal */}
      {selectedOption && (
        <Modal
          visible={isModalVisible}
          toggle={closeModal}
          modalContent={() => resolveModalContent()}
          customClass={styles.modalDimensions}
          headerText={options.find((o) => o.value === selectedValue)?.label || ''}
        />
      )}
    </Box>
  );
};

export default DropdownWithModal;
